import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/BookmarksPage.css';
import bookmarksData from '../data/bookmarks.json';

const BookmarksView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    // Check if user is authenticated via sessionStorage
    const authenticatedEmail = sessionStorage.getItem('bookmarksAuthEmail');
    
    if (authenticatedEmail) {
      setEmail(authenticatedEmail);
      setIsAuthorized(true);
    } else {
      // Redirect back if not authorized
      navigate('/');
    }
  }, [navigate]);

  // Handle scroll event to show/hide scroll-to-top button
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Show button when scrolled down more than 300px
      setShowScrollTop(scrollContainer.scrollTop > 300);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isAuthorized]);

  // Filter bookmarks based on search query (searches both category name and bookmarks)
  // Also filter out categories where visible is false
  const filteredBookmarks = bookmarksData
    .filter(category => category.visible !== false) // Only show visible categories
    .map(category => {
      const categoryMatches = category.category.toLowerCase().includes(searchQuery.toLowerCase());
      
      // If category name matches, show all bookmarks in that category
      if (categoryMatches && searchQuery.trim()) {
        return category;
      }
      
      // Otherwise, filter bookmarks by title or URL
      return {
        ...category,
        bookmarks: category.bookmarks.filter(bookmark =>
          bookmark.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          bookmark.url.toLowerCase().includes(searchQuery.toLowerCase())
        )
      };
    }).filter(category => category.bookmarks.length > 0);

  if (!isAuthorized) {
    return null; // Will redirect in useEffect
  }

  const handleBackToHome = () => {
    sessionStorage.removeItem('bookmarksAuthEmail');
    navigate('/');
  };

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bookmarks-fullpage" ref={scrollContainerRef}>
      <div className="bookmarks-main-container">
        {/* Compact Header with Search in Single Line */}
        <div className="bookmarks-compact-header">
          <button className="bookmarks-back-btn" onClick={handleBackToHome}>
            ← Back to Home
          </button>
          <h2 className="bookmarks-welcome">📚 Your Bookmarks</h2>
          <span className="bookmarks-user-email">Logged in: <strong>{email}</strong></span>
          <div className="bookmarks-search-inline">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="🔍 Search by category or bookmark..."
              className="bookmarks-search-input"
              autoFocus
            />
            {searchQuery && (
              <button 
                className="bookmarks-search-clear"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Bookmarks Content */}
        {filteredBookmarks.length === 0 ? (
          <div className="bookmarks-no-results">
            <p>No bookmarks found matching "{searchQuery}"</p>
          </div>
        ) : (
          <div className="bookmarks-categories">
            {filteredBookmarks.map((category, index) => (
              <div key={index} className="bookmarks-category">
                <div className="bookmarks-category-content">
                  <h3 className="bookmarks-category-title">{category.category}</h3>
                  <div className="bookmarks-pills-container">
                    {category.bookmarks.map((bookmark, i) => (
                      <span
                        key={i}
                        className="bookmark-pill"
                        onClick={() => window.open(bookmark.url, '_blank')}
                        title={bookmark.url}
                      >
                        {bookmark.title}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Horizontal divider line */}
                {index < filteredBookmarks.length - 1 && (
                  <div className="bookmarks-category-divider"></div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button className="scroll-to-top-btn" onClick={scrollToTop}>
          <span className="scroll-arrow">↑</span>
          <span className="scroll-text">Go Top</span>
        </button>
      )}
    </div>
  );
};

export default BookmarksView;
